import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  type ReactElement,
} from 'react';
import { Stack } from '@mantine/core';
import { NavigationTabs } from '@/components/common/NavigationTabs';
import { DetailPanelLayout } from '@/components/common/DetailPanelLayout';
import { DeleteConfirmationDialog } from '@/components/ui/DeleteConfirmationDialog';
import {
  useDetailOverlayFullscreen,
  useRegisterDetailSlots,
} from '@/components/overlays/UnifiedDetailOverlay';
import { TaskDetailsHeader, TaskDetailsContent } from './components';
import { FullscreenRightPanel } from './components/FullscreenRightPanel';
import type { TabType } from './components';
import { useDeviceLayout } from '@/hooks/useDeviceLayout';
import { useTaskHasNotes } from '@/hooks/useTaskHasNotes';
import { useTaskDetailsState } from './hooks/useTaskDetailsState';
import { useTaskDetailsPanelNavigation } from './hooks/useTaskDetailsPanelNavigation';
import { useTaskDetailsSectionTabs } from './hooks/useTaskDetailsSectionTabs';
import { useGeoMarkupStore, selectSelectedTask } from '@/store';
import { RIGHT_PANEL_MIN_WIDTH } from '@/constants/ui';
import { taskHasComments } from '@/utils/taskHelpers';
import styles from './TaskDetailsPanel.module.css';

/**
 * Detail content for the Task artifact, rendered inside
 * `UnifiedDetailOverlay`. Publishes header and toolbar callbacks to the
 * shell via `useRegisterDetailSlots`; reads fullscreen state from the
 * shared `DetailOverlayContext` so sidebar + right-panel layout stay in
 * sync with the shell's expand/collapse control.
 */
export function TaskDetailContent(): ReactElement | null {
  const selectedTask = useGeoMarkupStore(selectSelectedTask);
  const previewMode = useGeoMarkupStore(
    (state) => state.ui.slidePanel.previewMode
  );
  const setPreviewMode = useGeoMarkupStore((state) => state.ui.setPreviewMode);
  const fetchTaskById = useGeoMarkupStore((state) => state.tasks.fetchTaskById);
  const deleteTaskApi = useGeoMarkupStore((state) => state.tasks.deleteTaskApi);

  const {
    tabs,
    activeTabId,
    handleTabClick,
    handleTabClose,
    handleViewRelatedTask,
    handleViewNote,
    openArtifact,
    closeOverlay,
  } = useTaskDetailsPanelNavigation(selectedTask);

  const { isMobile, width } = useDeviceLayout();
  const { isFullscreenMode } = useDetailOverlayFullscreen();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const showSidebar = isFullscreenMode && !isMobile;
  const showRightPanel =
    isFullscreenMode && !isMobile && width >= RIGHT_PANEL_MIN_WIDTH;

  const hasAnyComments = selectedTask ? taskHasComments(selectedTask) : false;

  const { hasNotes, isLoading: notesLoading } = useTaskHasNotes(
    selectedTask?.id
  );

  const { activeTab, setActiveTab } = useTaskDetailsState(
    selectedTask?.id,
    selectedTask ?? undefined,
    {
      hideComments: showRightPanel || !hasAnyComments,
      hideNotes: !notesLoading && !hasNotes,
    }
  );

  useEffect(() => {
    if (selectedTask?.id) {
      fetchTaskById(selectedTask.id);
    }
  }, [selectedTask?.id, fetchTaskById]);

  const handleEdit = useCallback(() => {
    if (selectedTask) {
      openArtifact('tasks', 'edit', selectedTask.shortId);
    }
  }, [selectedTask, openArtifact]);

  const handleDeleteTask = useCallback(() => {
    setShowDeleteDialog(true);
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteDialog(false);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (selectedTask) {
      await deleteTaskApi(selectedTask.id);
      setShowDeleteDialog(false);
      closeOverlay();
    }
  }, [selectedTask, deleteTaskApi, closeOverlay]);

  const hasAttachments = (selectedTask?.attachments?.length ?? 0) > 0;
  const hasRequirements = (selectedTask?.requirements?.length ?? 0) > 0;
  const hasRelatedTasks = (selectedTask?.relatedTasks?.length ?? 0) > 0;
  const hasGeometries = (selectedTask?.geometries?.length ?? 0) > 0;

  const sectionTabs = useTaskDetailsSectionTabs({
    showRightPanel,
    hasNotes,
    notesLoading,
    hasAttachments,
    hasRequirements,
    hasRelatedTasks,
    hasGeometries,
    hasComments: hasAnyComments,
  });

  const handleSectionTabChange = useCallback(
    (tabId: string) => {
      const currentPreviewMode =
        useGeoMarkupStore.getState().ui.slidePanel.previewMode;
      if (isMobile && currentPreviewMode && tabId !== activeTab) {
        setPreviewMode(false);
      }
      setActiveTab(tabId as TabType);
    },
    [isMobile, activeTab, setPreviewMode, setActiveTab]
  );

  const headerContent = useMemo(() => {
    if (!selectedTask) return undefined;
    if (showSidebar) {
      return tabs.length > 1 ? (
        <NavigationTabs
          tabs={tabs}
          activeTabId={activeTabId}
          onTabClick={handleTabClick}
          onTabClose={handleTabClose}
        />
      ) : undefined;
    }
    return (
      <Stack gap="xs" className={styles.headerStack}>
        <NavigationTabs
          tabs={tabs}
          activeTabId={activeTabId}
          onTabClick={handleTabClick}
          onTabClose={handleTabClose}
        />

        <TaskDetailsHeader task={selectedTask} />
      </Stack>
    );
  }, [
    selectedTask,
    showSidebar,
    tabs,
    activeTabId,
    handleTabClick,
    handleTabClose,
  ]);

  useRegisterDetailSlots({
    headerContent,
    onEdit: selectedTask ? handleEdit : undefined,
    onDelete: selectedTask ? handleDeleteTask : undefined,
    fullscreenWideContent: showSidebar,
    defaultFullscreen: false,
    previewMode: previewMode && isMobile,
    onPreviewModeChange: setPreviewMode,
  });

  if (!selectedTask) {
    return null;
  }

  return (
    <>
      <DetailPanelLayout
        mode={showSidebar ? 'sidebar' : 'tabs'}
        sectionItems={sectionTabs}
        activeSection={activeTab}
        onSectionChange={handleSectionTabChange}
        sidebarHeader={<TaskDetailsHeader task={selectedTask} />}
        aside={
          showRightPanel ? (
            <FullscreenRightPanel
              task={selectedTask}
              showComments={hasAnyComments}
            />
          ) : undefined
        }
        contentVariant={
          activeTab === 'comments'
            ? 'wide'
            : activeTab === 'history'
              ? 'flex'
              : 'default'
        }
        widePreview
      >
        <TaskDetailsContent
          activeTab={activeTab}
          task={selectedTask}
          onViewRelatedTask={handleViewRelatedTask}
          onViewNote={handleViewNote}
          setActiveTab={setActiveTab}
        />
      </DetailPanelLayout>

      <DeleteConfirmationDialog
        isOpen={showDeleteDialog}
        itemName={selectedTask.title}
        itemType="task"
        onCancel={handleDeleteCancel}
        onConfirm={confirmDelete}
      />
    </>
  );
}
